import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UseHoverDisclosureOptions {
  closeDelay?: number;
}

export const useHoverDisclosure = ({
  closeDelay = 120,
}: UseHoverDisclosureOptions = {}) => {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), closeDelay);
  }, [clearCloseTimer, closeDelay]);

  useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, [clearCloseTimer]);

  const triggerProps = useMemo(
    () => ({
      onMouseEnter: openNow,
      onMouseLeave: scheduleClose,
    }),
    [openNow, scheduleClose],
  );

  const contentProps = useMemo(
    () => ({
      onMouseEnter: openNow,
      onMouseLeave: scheduleClose,
    }),
    [openNow, scheduleClose],
  );

  return {
    open,
    setOpen,
    openNow,
    scheduleClose,
    triggerProps,
    contentProps,
  };
};
